import type { ScenarioSpec } from "./types";

/**
 * Max Works Remotely… Unfortunately
 *
 * Flagship multi-scene course: Max Berger (sales manager) travels to a client
 * meeting and the learner steers him through pack / public Wi-Fi / shoulder
 * surfing / public calls / lost laptop / juice-jacking / BYOD / deepfake CEO.
 * Risk Meter tracks cumulative mistakes; Scene 9 reviews the day's timeline.
 *
 * Environments CUT: apartment → platform → carriage → platform → café →
 * lobby → meeting. Apartment uses image→Marble world ca4cdba414db61bd
 * (from gpt-image still apt_pack). Other scenes stay procedural until their
 * still→Marble pass lands. CEO deepfake clips: /cdn/sim/remote-work/clips/*
 */

const CDN = "/cdn/sim/remote-work";
const CEO_AVATAR = `${CDN}/img/ceo.png`;

const CEO_LINES = {
  greeting:
    "Max — glad I caught you. This is urgent and it has to stay between us.",
  request:
    "Emergency. Send me the client pricing file on WhatsApp right now. Also, approve the login prompt you just received on your phone.",
  pressure:
    "Max, I don't have time for process. The board is waiting. Send the file and approve the MFA — now.",
  deflect:
    "The Zurich project code? Max, you can see me. Just send the file. We can talk about codes later.",
  goodbyeFail:
    "Good. I knew I could count on you. Don't mention this to anyone.",
  goodbyeVerify:
    "Fine — do whatever you think you need to do. Just don't miss the window.",
};

export const remoteWorkMaxScenario: ScenarioSpec = {
  version: 1,
  id: "remote-work-max",
  title: "Max Works Remotely\u2026 Unfortunately",
  summary: {
    goal: "Learn to keep company data safe on the move: devices, public Wi-Fi, screens and calls in public, lost laptops, chargers, personal devices and impersonation.",
    youDo: "Pack Max's bag, choose a station Wi-Fi network, protect his screen and calls on the train, react to a lost laptop, charge his phone, set up a personal tablet, judge a video call from the CEO and leave a shared PC, then watch a short film on working on the road.",
    duration: "19-24 min",
    tags: ["remote_work", "public_wifi", "personal_devices", "deepfakes", "usb_devices", "incident_reporting"],
    audience: ["remote_workers", "travellers"],
    needs: ["sound"],
  },
  topic: "Remote work, mobile & BYOD security",
  language: "en",
  // Apartment: a real easy-listening track from the Incompetech free library.
  // "Carefree" by Kevin MacLeod, CC BY 4.0; attribution in public/audio/CREDITS.md.
  ambientUrl: "/audio/carefree-kevin-macleod.mp3",
  ambientVolume: 0.075,
  music: "none",
  riskMeter: true,

  scene: {
    environment: {
      // Image→Marble from approved apt_pack still (world ca4cdba414db61bd).
      // Pack props: sticky, USB, headset, backpack, tablet, router.
      splat: {
        high: { url: "/cdn/sim/worlds/ca4cdba414db61bd/world_2m.spz" },
        mid: { url: "/cdn/sim/worlds/ca4cdba414db61bd/world_500k.spz" },
        low: { url: "/cdn/sim/worlds/ca4cdba414db61bd/pano.jpg" },
      },
      enablePano: true,
      enableSplat: false,
      panoUrl: "/cdn/sim/worlds/ca4cdba414db61bd/pano.jpg",
      // With corrected pano orbit (look -Z): 180° puts dual-monitor desk ahead.
      panoRotationYDeg: 180,
      // Calibrated: markers anchored by measured equirect texels sit on the real
      // objects at this offset (desk cluster verified against the pano).
      packPinYawOffsetDeg: 24,
      procedural: "apartment",
      backdropColor: "#1a1814",
      exposure: 2.0,
    },
    environments: [
      {
        id: "apartment",
        environment: {
          splat: {
            high: { url: "/cdn/sim/worlds/ca4cdba414db61bd/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/ca4cdba414db61bd/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/ca4cdba414db61bd/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/ca4cdba414db61bd/pano.jpg",
          panoRotationYDeg: 180,
          packPinYawOffsetDeg: 24,
          procedural: "apartment",
          backdropColor: "#1a1814",
          exposure: 2.0,
        },
        music: "none",
        ambientUrl: "/audio/carefree-kevin-macleod.mp3",
        ambientVolume: 0.075,
      },
      {
        id: "platform_kiosk",
        environment: {
          // Approved front-facing kiosk world. Scene 2 uses this view; Scene 4
          // keeps the original platform/train world.
          splat: {
            high: { url: "/cdn/sim/worlds/12d0a12d4b02fe56/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/12d0a12d4b02fe56/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/12d0a12d4b02fe56/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/12d0a12d4b02fe56/pano.jpg",
          panoRotationYDeg: 90,
          procedural: "train_platform",
          backdropColor: "#8a96a4",
        },
        music: "none",
        ambientUrl: "/audio/train-station-eaglechopper.mp3",
        // MUCH louder on purpose: a busy station platform is a loud place, and
        // this was reported as "can barely hear it at max volume" three times.
        // fadeOfficeTo() settles ambience at 45% of this value after 7s, so
        // 0.85 settles around 0.38 — clearly audible, not a background hush.
        ambientVolume: 0.85,
      },
      {
        id: "platform",
        environment: {
          // Image->Marble photoreal pano from scene_platform still (world
          // f270c8201d62197a). Locked look-around at capture centre; procedural
          // train_platform is the fallback only.
          splat: {
            high: { url: "/cdn/sim/worlds/f270c8201d62197a/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/f270c8201d62197a/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/f270c8201d62197a/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/f270c8201d62197a/pano.jpg",
          panoRotationYDeg: 202,
          procedural: "train_platform",
          backdropColor: "#8a96a4",
        },
        music: "none",
        ambientUrl: "/audio/train-station-eaglechopper.mp3",
        ambientVolume: 0.1,
      },
      {
        id: "carriage",
        environment: {
          // Image->Marble photoreal pano, v2 (world f1e3c7798f0b642d, generated
          // 2026-07-16 from a purpose-built seed image: Max's own open laptop +
          // coffee cup on HIS seatback tray table in the foreground, a fully
          // occupied carriage behind/around him, and an explicit anti-hallucination
          // prompt forbidding freestanding/floating tables in the aisle. Replaces
          // world d0c8b3c250f174f3, which had a facial-render glitch on one
          // passenger AND -- the reported issue -- lacked the "Max's own laptop at
          // his seat" framing the slide copy promises ("The Shoulder Surfing
          // Championship... Max wants to edit the confidential pricing proposal").
          // Marble's own caption confirms the fix: "the aisle remains completely
          // clear and unobstructed... individual tray tables... uniformly attached
          // to the seatbacks... no freestanding furniture impedes the path."
          // Verified clean across a full yaw sweep (-160..180 in 20deg steps) via
          // ops/_pano_preview.html + ops/_qa_pano_preview_shots.mjs /
          // _qa_pano_sweep2.mjs -- no floating tables, no anatomy glitches at any
          // angle.
          splat: {
            high: { url: "/cdn/sim/worlds/f1e3c7798f0b642d/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/f1e3c7798f0b642d/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/f1e3c7798f0b642d/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/f1e3c7798f0b642d/pano.jpg",
          // Calibrated 2026-07-16 against the ACTUAL gameplay camera
          // (wp_train_passenger). RE-TIGHTENED same day after a learner report
          // of "a table in the middle of the aisle": at the OLD fov=44 the two
          // seat-backs immediately beside the capture point filled the entire
          // frame when dragged toward (~70deg and ~130deg), losing the
          // headrest/armrest/floor that would otherwise identify them as
          // ordinary seats -- misread as a flat floating object. Confirmed via
          // ops/_crop_pano.sh (8-way 360 crop of the raw pano.jpg, zoomed): every
          // seat has a normal silhouette, there is NO duplicate/floating
          // furniture anywhere in the source image. Fix is two-part: (1)
          // wp_train_passenger.fov widened 44->64 so those close seats keep
          // their recognisable proportions in frame, (2) yaw range tightened
          // from +/-45 to +/-30 so the default drag range stays centred on the
          // laptop+aisle content and doesn't reach all the way to either close
          // seat. Re-verified via ops/qa-fine-sweep.mjs across the full range.
          panoRotationYDeg: 85,
          panoYawLimitDeg: 30,
          procedural: "train_carriage",
          backdropColor: "#9aa4b0",
        },
        // Real train-interior sound (synthesized rumble + rail-joint clacks),
        // NOT the station platform recording — this is a moving carriage.
        music: "train",
        ambientUrl: "",
      },
      {
        id: "cafe",
        environment: {
          // Approved café world: real tablet + personal charger on Max's table,
          // separate wall AC outlet and public USB panel/cable.
          splat: {
            high: { url: "/cdn/sim/worlds/ab62357bb52aeb8d/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/ab62357bb52aeb8d/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/ab62357bb52aeb8d/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/ab62357bb52aeb8d/pano.jpg",
          panoRotationYDeg: 90,
          packPinYawOffsetDeg: 0,
          procedural: "restaurant",
          backdropColor: "#cfe6f2",
          props: { signText: "Station Café", signSub: "Open" },
        },
        music: "none",
        // The original recording measured mean -56.5dB (essentially silent even
        // at max device volume) — a boosted +24dB render is what's actually
        // shipped. See sim3d/public/audio/cafe-ambience-boosted.mp3.
        // Lowered 2026-07-13 (was 0.55, loudest of any scene in this course
        // and reported as competing with the narration) -- still audible
        // cafe atmosphere without drowning out speech.
        ambientUrl: "/audio/cafe-ambience-boosted.mp3",
        ambientVolume: 0.4,
      },
      {
        id: "lobby",
        environment: {
          // Image->Marble photoreal pano from scene_lobby still (world
          // 90598377da00edd9). Reception + lounge framed front.
          splat: {
            high: { url: "/cdn/sim/worlds/90598377da00edd9/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/90598377da00edd9/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/90598377da00edd9/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/90598377da00edd9/pano.jpg",
          panoRotationYDeg: 210,
          procedural: "hotel_suite",
          backdropColor: "#bfe9f7",
        },
        music: "none",
        // Boosted +10dB render — the original measured mean -46dB (too quiet).
        ambientUrl: "/audio/hotel-lobby-boosted.mp3",
        ambientVolume: 0.5,
      },
      {
        id: "business_center",
        environment: {
          // Image->Marble photoreal pano from scene_business_center still
          // (world d7ba2e5a4c2ab867). Monitor framed dead-centre, screen off.
          splat: {
            high: { url: "/cdn/sim/worlds/d7ba2e5a4c2ab867/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/d7ba2e5a4c2ab867/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/d7ba2e5a4c2ab867/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/d7ba2e5a4c2ab867/pano.jpg",
          panoRotationYDeg: 180,
          procedural: "office",
          backdropColor: "#3a2e22",
        },
        music: "none",
        ambientUrl: "/audio/hotel-lobby-boosted.mp3",
        ambientVolume: 0.45,
      },
      {
        id: "meeting",
        environment: {
          // Image->Marble photoreal pano from scene_meeting still (world
          // 91e50b49018116c5). Boardroom table + wall screen framed front.
          splat: {
            high: { url: "/cdn/sim/worlds/91e50b49018116c5/world_2m.spz" },
            mid: { url: "/cdn/sim/worlds/91e50b49018116c5/world_500k.spz" },
            low: { url: "/cdn/sim/worlds/91e50b49018116c5/pano.jpg" },
          },
          enablePano: true,
          enableSplat: false,
          panoUrl: "/cdn/sim/worlds/91e50b49018116c5/pano.jpg",
          panoRotationYDeg: 180,
          procedural: "office",
          backdropColor: "#d8e0e8",
        },
        music: "office",
        ambientUrl: "",
        ambientVolume: 0.14,
      },
    ],
    props: [],
    waypoints: [
      // Cinematic pattern for EVERY scene: a WIDE "we arrive" establish (high fov)
      // then a decisive PUSH-IN (low fov, easeOut) onto the subject before the
      // decision — the movie feel. Pano scenes are locked at the sphere centre,
      // so the push-in is fov-only (no dolly) + a re-aim at the subject.
      {
        id: "wp_apt_establish",
        position: [0, 1.5, 0],
        target: [-0.15, 1.2, -1],
        fov: 80,
        durationS: 1.8,
        ease: "easeInOut",
      },
      {
        id: "wp_apt_desk",
        position: [0, 1.5, 0],
        // Push in on the desk cluster (still wide enough to tap the pack items).
        target: [-0.4, 1.26, -0.92],
        fov: 50,
        durationS: 1.7,
        ease: "easeOut",
      },
      // --- platform (pano: camera locked at sphere centre; only look dir varies) ---
      {
        id: "wp_plat_establish",
        position: [0, 1.5, 0],
        target: [0, 1.45, -1],
        // Wide establishing shot: the real kiosk stands in the station.
        fov: 70,
        durationS: 0,
        ease: "easeInOut",
      },
      {
        id: "wp_plat_kiosk",
        // First push: approach the full physical terminal.
        position: [0, 1.5, 0],
        target: [0, 1.45, -1],
        fov: 24,
        durationS: 1.6,
        ease: "easeOut",
      },
      {
        id: "wp_plat_screen",
        // Final push: the terminal's baked touchscreen fills the frame. WifiKiosk
        // is sized to this screen, so its wake/network UI appears ON the kiosk.
        position: [0, 1.5, 0],
        target: [0, 1.45, -1],
        fov: 8,
        durationS: 1.35,
        ease: "easeOut",
      },
      // --- carriage (Marble pano: camera locked at sphere centre; forward-only) ---
      {
        id: "wp_train_establish",
        position: [0, 1.5, 0],
        target: [0, 1.42, -1],
        fov: 78,
        durationS: 0,
        ease: "easeInOut",
      },
      {
        id: "wp_train_seat",
        position: [0, 1.5, 0],
        target: [0, 1.3, -1],
        fov: 48,
        durationS: 1.6,
        ease: "easeOut",
      },
      {
        id: "wp_train_passenger",
        // fov WIDENED 44->64 (2026-07-16): at fov=44 the two seat-backs that sit
        // immediately beside the camera's capture point filled the ENTIRE frame
        // edge-to-edge when the learner dragged toward them, with no headrest/
        // armrest/floor visible for context -- reads as an unrecognisable flat
        // blue slab ("a table in the aisle") even though the raw pano has no
        // such object (verified via ops/_crop_pano.sh: 8-way 360 crop of the
        // source pano.jpg, every seat has a normal headrest+armrest+floor, no
        // duplicate/floating furniture anywhere). Wider fov keeps those same
        // seats fully in view with their proportions legible.
        position: [0, 1.5, 0],
        target: [0.7, 1.35, -0.8],
        fov: 64,
        durationS: 1.6,
        ease: "easeOut",
      },
      {
        id: "wp_lost_train",
        // On the platform: push in toward the standing train (to the right of the
        // kiosk in world f270c8201d62197a) — it's leaving with Max's bag on board.
        position: [0, 1.5, 0],
        target: [0.9, 1.4, -0.5],
        fov: 52,
        durationS: 1.7,
        ease: "easeOut",
      },
      // --- cafe (pano) ---
      {
        id: "wp_cafe_establish",
        position: [0, 1.5, 0],
        target: [0, 1.42, -1],
        fov: 78,
        durationS: 0,
        ease: "easeInOut",
      },
      {
        id: "wp_cafe_table",
        // Push in on Max's table + the charging options (wide enough to tap objects).
        position: [0, 1.5, 0],
        target: [0, 1.28, -1],
        fov: 52,
        durationS: 1.6,
        ease: "easeOut",
      },
      {
        id: "wp_byod_table",
        // Push down onto the table where the personal tablet sits (Scene 6).
        position: [0, 1.5, 0],
        target: [0, 1.12, -1],
        fov: 44,
        durationS: 1.5,
        ease: "easeOut",
      },
      // --- lobby (pano) ---
      {
        id: "wp_lobby_establish",
        position: [0, 1.5, 0],
        target: [0, 1.42, -1],
        fov: 76,
        durationS: 0,
        ease: "easeInOut",
      },
      {
        id: "wp_lobby_phone",
        // Push in as the phone rings (the deepfake call).
        position: [0, 1.5, 0],
        target: [0, 1.4, -1],
        fov: 42,
        durationS: 1.7,
        ease: "easeOut",
      },
      // --- business centre (still-image cinematic zoom onto the monitor) ---
      {
        id: "wp_biz_establish",
        position: [0, 1.5, 0],
        target: [0, 1.42, -1],
        fov: 76,
        durationS: 0,
        ease: "easeInOut",
      },
      {
        id: "wp_biz_approach",
        position: [0, 1.5, 0],
        target: [0, 1.42, -1],
        fov: 40,
        durationS: 1.6,
        ease: "easeOut",
      },
      {
        id: "wp_biz_screen",
        position: [0, 1.5, 0],
        target: [0, 1.42, -1],
        fov: 14,
        durationS: 1.3,
        ease: "easeOut",
      },
      // --- meeting (pano) ---
      {
        id: "wp_meet_establish",
        position: [0, 1.5, 0],
        target: [0, 1.4, -1],
        fov: 70,
        durationS: 0,
        ease: "easeInOut",
      },
    ],
    initialWaypointId: "wp_apt_establish",
  },

  devices: [
    {
      id: "pack",
      kind: "packlist",
      config: {
        headline: "Pack for the trip",
        body: "Look around Max's apartment. Tap what belongs in his bag for remote work — then confirm.",
        voiceId: "onyx",
        language: "en-US",
        // plateUrl marks pack-in-3D mode (Marble pano look-around + projected pins).
        // xPct/yPct are desk-facing still anchors mapped onto the sphere.
        plateUrl: "/cdn/sim/remote-work/env-refs/apt_pack.png",
        // Bare labelled markers (NO sprite cutouts) pointing at the real objects
        // baked into world ca4cdba414db61bd (pano 4608x2304). u/v are MEASURED
        // equirect texels along the real desk surface (v≈0.60–0.69) + the bed, so
        // each marker sits ON a baked object and tracks the look-around with no
        // floating layer. packPinYawOffsetDeg (env) aligns the cluster.
        // Marble baked only the big furniture; the small pack props are composited
        // as grounded transparent cutouts (/cdn/sim/remote-work/props/*) at each
        // marker so every item is a REAL visible object on the desk (not a label
        // over empty wood). spriteAnchor:"bottom" sits the object base on the pin.
        plateHotspots: [
          {
            id: "hs_laptop",
            label: "Work laptop",
            role: "safe",
            required: true,
            detail: "Company laptop — the right place for the client deck.",
            spriteUrl: "/cdn/sim/remote-work/props/laptop.png",
            spriteWidthPct: 12,
            spriteAnchor: "bottom",
            u: 0.3,
            v: 0.665,
            xPct: 28,
            yPct: 52,
            reveal: {
              kind: "lock_screen",
              deviceLabel: "LAPTOP-4521 · Northwind AG",
              deviceCaption: "Encrypted disk · Remote-wipe enabled · Asset tracked",
              narrative:
                "This is the company-managed device — full-disk encryption, MDM enrollment, and IT can remote-lock or wipe it in minutes if it's lost. This is where the client deck belongs, not on a personal copy.",
            },
          },
          {
            id: "hs_privacy",
            label: "Privacy filter",
            role: "safe",
            required: true,
            detail:
              "A privacy screen stops shoulder-surfing on public transport. Pack it with the laptop.",
            spriteUrl: "/cdn/sim/remote-work/props/privacy_filter.png",
            spriteWidthPct: 9,
            spriteAnchor: "bottom",
            u: 0.335,
            v: 0.64,
            xPct: 36,
            yPct: 38,
            reveal: {
              kind: "product",
              title: "14–16” privacy filter",
              specs: ["Blocks side view beyond 30°", "Matte anti-glare", "Peels on, no tools"],
              narrative:
                "Darkens the screen for anyone not sitting dead-center in front of it. On a train or in a lounge, this is what stops the passenger next to you from reading pricing over your shoulder.",
            },
          },
          {
            id: "hs_charger",
            label: "Charger",
            role: "safe",
            required: true,
            detail: "Dead battery mid-proposal is an avoidable incident. Take the charger.",
            spriteUrl: "/cdn/sim/remote-work/props/charger.png",
            spriteWidthPct: 8,
            spriteAnchor: "bottom",
            u: 0.365,
            v: 0.675,
            xPct: 22,
            yPct: 58,
            reveal: {
              kind: "product",
              title: "Genuine 65W charger",
              specs: ["Data pins disabled", "Matches laptop wattage", "Yours end-to-end"],
              narrative:
                "Hardware you control, start to finish. Later today Max will be tempted by a public USB port at a café — this is the entire reason that trap doesn't work: bring your own power and it's a non-issue.",
            },
          },
          {
            id: "hs_headset",
            label: "Headset",
            role: "safe",
            required: true,
            detail: "For the video call on the train without broadcasting the deal to the carriage.",
            spriteUrl: "/cdn/sim/remote-work/props/headset.png",
            spriteWidthPct: 8,
            spriteAnchor: "bottom",
            u: 0.4,
            v: 0.67,
            xPct: 48,
            yPct: 44,
            reveal: {
              kind: "product",
              title: "Noise-isolating headset",
              specs: ["Boom mic", "In-ear seal", "Keeps your side quiet"],
              narrative:
                "Muffles the other side of a call so the carriage doesn't hear discount numbers or a CFO's name. It doesn't excuse saying confidential details out loud — but it's the first line of defense on a public call.",
            },
          },
          {
            id: "hs_usb",
            label: "USB stick",
            role: "trap",
            detail:
              "A random USB with the full client folder is how leaks start. Leave it.",
            spriteUrl: "/cdn/sim/remote-work/props/usb_stick.png",
            spriteWidthPct: 5,
            spriteAnchor: "bottom",
            u: 0.43,
            v: 0.665,
            xPct: 30,
            yPct: 56,
            reveal: {
              kind: "file_explorer",
              explorerTitle: "USB Drive (E:)",
              files: [
                { name: "Client_Contracts_2026.xlsx", size: "2.4 MB", kind: "sheet", sensitive: true },
                { name: "Q3_Pricing_Master.pdf", size: "1.1 MB", kind: "pdf", sensitive: true },
                { name: "passwords.txt", size: "2 KB", kind: "text", sensitive: true },
                { name: "VPN_configs.zip", size: "640 KB", kind: "archive" },
              ],
              narrative:
                "This is what's actually on the stick: the full client folder, unencrypted, opens with zero login. If it falls out of a pocket on the train, whoever finds it has the contracts and the pricing — no hacking required.",
            },
          },
          {
            id: "hs_sticky",
            label: "Sticky note",
            role: "trap",
            detail:
              "Password on a sticky note travels with the laptop. Do not pack that habit.",
            spriteUrl: "/cdn/sim/remote-work/props/sticky_note.png",
            spriteWidthPct: 5,
            spriteAnchor: "bottom",
            u: 0.46,
            v: 0.675,
            xPct: 33,
            yPct: 42,
            reveal: {
              kind: "sticky_note",
              noteLines: ["Laptop pwd:", "Th3Deal2024!", "(don't forget!)"],
              narrative:
                "This is the actual note — Max's real laptop password, handwritten and left in plain view. If this travels with the bag, anyone who finds the laptop now also has the key to unlock it. Leave it at home, use a password manager instead.",
            },
          },
          {
            id: "hs_tablet",
            label: "Personal tablet",
            role: "trap",
            detail:
              "Client files on a personal tablet is a walking data breach. Leave it behind.",
            spriteUrl: "/cdn/sim/remote-work/props/tablet.png",
            spriteWidthPct: 8,
            spriteAnchor: "bottom",
            u: 0.49,
            v: 0.67,
            xPct: 54,
            yPct: 56,
            reveal: {
              kind: "device_home",
              homeBanner: "iCloud backup: 3 new photos synced to personal account",
              homeApps: [
                { label: "Client CRM", tone: "work" },
                { label: "Photos", tone: "alert" },
                { label: "Instagram", tone: "personal" },
                { label: "Candy Match", tone: "personal" },
                { label: "Banking", tone: "personal" },
                { label: "TikTok", tone: "personal" },
                { label: "WhatsApp", tone: "personal" },
                { label: "Notes", tone: "personal" },
              ],
              narrative:
                "This is the actual home screen: a work CRM app sitting next to games, social apps, and a personal photo backup. No MDM, no remote wipe, and every screenshot of a client file can land in that same personal iCloud backup.",
            },
          },
        ],
        lenaSafe:
          "Good. Laptop, charger, privacy filter, headset — and nothing personal. You may enter public transport without becoming a walking data breach.",
        lenaRisky:
          "Personal tablets, USB copies, or password sticky notes are not 'being prepared.' They are how incident reports are born. Missing protection gear is just as bad.",
        // Legacy menu kept for schema compat; unused when plateUrl is set.
        options: [
          {
            id: "a",
            label: "Laptop, phone, charger, privacy screen, headset",
            category: "verify",
            eventId: "packed_safe",
            outcome: "safe",
            safe: true,
          },
          {
            id: "b",
            label: "Laptop and phone only — security gear is heavy",
            category: "comply",
            eventId: "packed_personal",
            outcome: "risky",
          },
          {
            id: "c",
            label: "Personal tablet with copied client files",
            category: "comply",
            eventId: "packed_personal",
            outcome: "risky",
          },
          {
            id: "d",
            label: "USB stick with the full client folder",
            category: "comply",
            eventId: "packed_usb",
            outcome: "risky",
          },
        ],
      },
    },
    {
      id: "wifi",
      kind: "wifikiosk",
      config: {
        kioskName: "Free Wi-Fi \u00b7 Station terminal",
        headline: "Available networks",
        body: "Welcome to the free Wi-Fi terminal. Please choose the best plan for your trip.",
        voiceId: "nova",
        language: "en-US",
        networks: [
          {
            id: "sbb_official",
            ssid: "SBB-Free",
            signal: 95,
            encrypted: false,
            trusted: true,
            category: "verify",
            eventId: "wifi_official",
            outcome: "safe",
          },
          {
            id: "sbb_fast",
            ssid: "SBB-Free-WiFi-Fast",
            signal: 88,
            encrypted: false,
            trap: true,
            trapKind: "evil_twin",
            category: "comply",
            eventId: "wifi_trap",
            outcome: "risky",
          },
          {
            id: "station_secure",
            ssid: "Station-Secure",
            signal: 74,
            encrypted: true,
            trap: true,
            trapKind: "creds",
            category: "comply",
            eventId: "wifi_trap",
            outcome: "risky",
          },
          {
            id: "clientvip",
            ssid: "ClientMeetingVIP",
            signal: 61,
            encrypted: true,
            trap: true,
            trapKind: "creds",
            category: "comply",
            eventId: "wifi_trap",
            outcome: "risky",
          },
          {
            id: "coffee_guest",
            ssid: "CoffeeBarGuest",
            signal: 52,
            encrypted: false,
            trap: true,
            trapKind: "evil_twin",
            category: "comply",
            eventId: "wifi_trap",
            outcome: "risky",
          },
        ],
        realPortal: {
          brand: "SBB CFF FFS",
          url: "https://sbb.ch/wifi",
          headline: "Free on-board & station Wi-Fi",
          body: "No password, no card. Just continue as a guest to get online.",
          payLabel: "Continue as guest \u2014 connect",
        },
        fakePortal: {
          brand: "SBB Rail Wi-Fi",
          url: "http://sbb-freewifi.login-portal.net/pay",
          legitUrl: "https://sbb.ch",
          headline: "Verifiy your card to continue browsing",
          cardLabel: "Card number (required to connect)",
          flaws: [
            "The address bar shows sbb-freewifi.login-portal.net \u2014 not sbb.ch.",
            "No padlock: the connection is not secure (http, not https).",
            "'Verifiy' is misspelled \u2014 real operators proofread their pages.",
            "Real guest Wi-Fi never asks for a credit card to connect.",
          ],
        },
        certError: {
          title: "Your connection is not private",
          body: "Certificate mismatch for sbb.ch \u2014 someone on this network is intercepting your traffic.",
          errorCode: "NET::ERR_CERT_AUTHORITY_INVALID",
        },
        verifyTips: [
          "Real station Wi-Fi is usually OPEN and just asks you to continue as a guest \u2014 never for a password or card.",
          "Read the address bar: the official page is sbb.ch, not a look-alike like sbb-freewifi.login-portal.net.",
          "Valid padlock + correct domain = safe. A warning triangle, http, or misspelt words = walk away.",
          "When unsure, use your own phone hotspot or a company VPN.",
        ],
        lenaSafe:
          "Open, official, and it just asks you to continue as a guest on sbb.ch. That is how real station Wi-Fi behaves.",
        lenaRisky:
          "That was not the railway. Wrong domain, no padlock, and it wanted a card. Now every site Max opens is being intercepted.",
      },
    },
    {
      id: "shoulder",
      kind: "shouldersurf",
      config: {
        headline: "The Shoulder Surfing Championship",
        body: "Max wants to edit the confidential pricing proposal. The carriage is busy and several passengers have a clear sightline to his screen.",
        requireInspect: false,
        comicFail:
          "Congratulations. You have just crowdsourced your pricing strategy.",
        lenaSafe:
          "Privacy screen, better seat, fewer spectators. Max almost looked professional.",
        voiceId: "onyx",
        language: "en-US",
        options: [
          {
            id: "a",
            label: "Open the pricing document immediately",
            category: "comply",
            eventId: "shoulder_fail",
            // 2026-07-14: each wrong option now gets its OWN outcome key so
            // it routes to its OWN teach beat below (b_teach_shoulder_open/
            // _zoom/_bright) instead of one generic "risky" screen shared by
            // all three -- see edges + those beat defs further down.
            outcome: "risky_open",
          },
          {
            id: "b",
            label: "Move to a more private seat and use the privacy screen",
            category: "verify",
            eventId: "shoulder_safe",
            outcome: "safe",
            safe: true,
          },
          {
            id: "c",
            label: "Zoom in — the small text is hard to read",
            category: "comply",
            eventId: "shoulder_fail",
            outcome: "risky_zoom",
          },
          {
            id: "d",
            label: "Turn brightness up to maximum",
            category: "comply",
            eventId: "shoulder_fail",
            outcome: "risky_bright",
          },
        ],
      },
    },
    {
      id: "pubcall",
      kind: "publiccall",
      config: {
        callerName: "Nina Keller",
        callerSubtitle: "Colleague · Sales ops",
        opener:
          "Can you confirm the final discount and the client's internal contact?",
        lenaSafe:
          "Beautiful. A rare moment where Max remembered public means public.",
        voiceId: "onyx",
        language: "en-US",
        options: [
          {
            id: "a",
            label:
              "Yes, we offer them 32%. Their CFO Martin Keller is pushing procurement.",
            category: "comply",
            eventId: "call_disclose",
            outcome: "risky",
            replyLine: "Okay, thanks. I'll update the proposal.",
          },
          {
            id: "b",
            label: "I'm on a train. I'll call you back when I'm somewhere private.",
            category: "verify",
            eventId: "call_safe",
            outcome: "safe",
            safe: true,
            replyLine: "Perfect — call me when you're somewhere private.",
          },
          {
            id: "c",
            label: "I can talk, but let me use my headset and keep it general.",
            category: "question",
            eventId: "call_safe",
            outcome: "safe",
            safe: true,
            replyLine:
              "We should keep the previous commercial structure. I'll confirm details later.",
          },
          {
            id: "d",
            label: "Wait, let me put you on speaker. The train is loud.",
            category: "comply",
            eventId: "call_disclose",
            outcome: "risky",
            replyLine: "All right. Put me on speaker.",
          },
        ],
      },
    },
    {
      id: "lost",
      kind: "lostdevice",
      config: {
        headline: "The laptop that wanted freedom",
        body: "Max rushed off onto the platform — but his laptop bag is still on the departing train. What should he do first?",
        bagHotspot: {
          id: "lost_bag_behind",
          label: "Laptop bag",
          detail: "Max got lucky: someone placed the forgotten bag safely behind him.",
          role: "info",
          // Measured equirect texel: solid platform ground on MAX'S OWN side, next
          // to the kiosk — not across the tracks. u/v (exact) wins over yaw/pitch.
          u: 0.44,
          v: 0.72,
          xPct: 50,
          yPct: 54,
          spriteUrl: "/cdn/sim/remote-work/props/laptop_bag_found.png",
          spriteWidthPct: 11,
          spriteAnchor: "bottom",
        },
        lenaSafe:
          "Device reported. Remote lock requested. Token sessions revoked. This is why early reporting matters.",
        lenaRisky:
          "Please do not turn a security incident into a public marketing campaign.",
        voiceId: "onyx",
        language: "en-US",
        options: [
          {
            id: "found_bag",
            label: "Pick up the laptop bag",
            category: "verify",
            eventId: "lost_found",
            outcome: "safe",
            safe: true,
            hiddenInList: true,
            replyLine: "Max got lucky. The bag is still here. Check it, secure it, and report the near miss.",
          },
          {
            id: "wait",
            label: "Wait and see if someone returns it",
            category: "comply",
            eventId: "lost_wait",
            // 2026-07-14: distinct outcome keys per wrong choice so each
            // routes to its own tailored teach beat -- see edges + the
            // b_teach_lost_* beats further down.
            outcome: "risky_wait",
          },
          {
            id: "report",
            label: "Call IT/security immediately and report the lost device",
            category: "verify",
            eventId: "lost_report",
            outcome: "safe",
            safe: true,
          },
          {
            id: "linkedin",
            label: "Post on LinkedIn: \"Lost laptop, anyone seen it?\"",
            category: "comply",
            eventId: "lost_linkedin",
            outcome: "risky_linkedin",
          },
          {
            id: "delay",
            label: "Ask the client to delay and say nothing internally",
            category: "refuse",
            eventId: "lost_wait",
            outcome: "risky_delay",
          },
        ],
      },
    },
    {
      id: "charger",
      kind: "cafecharge",
      config: {
        headline: "The Charger of Doom",
        body: "Phone at 4%. A public USB port is right there on the wall.",
        safeBody: "Now that you've seen what a bad cable does, charge Max's phone the safe way instead.",
        goblinCaption: "Malicious USB firmware installed. Keylogger active. Files copied to an unknown host.",
        lenaSafe: "Own cable, own power. Nothing to intercept, nothing to install.",
        lenaRisky:
          "That USB port can run BadUSB \u2014 a tiny hidden chip that acts like a keyboard the instant you plug in. It types its own commands, installs a keylogger, and copies files. No file needed opening. Just plugging in was the entire attack.",
        voiceId: "onyx",
        language: "en-US",
        // plateUrl marks pano-pin mode. u/v are MEASURED equirect texels of the
        // real baked objects in the cafe world (calibrated after the render).
        plateUrl: "/cdn/sim/remote-work/env-refs/scene_cafe.png",
        hotspots: [
          {
            id: "hs_powerbank",
            label: "Max's own charger",
            detail: "His own AC charger and cable are on the foreground table. Known gear under his control.",
            safe: true,
            eventId: "charger_safe",
            outcome: "safe",
            pickLine: "Own charger, own cable. Nothing to intercept.",
            u: 0.503,
            v: 0.656,
            xPct: 34,
            yPct: 60,
          },
          {
            id: "hs_outlet",
            label: "Wall outlet",
            detail: "A proper AC power outlet on the far wall with his own charger. Safe, just a walk away from his seat.",
            safe: true,
            eventId: "charger_safe",
            outcome: "safe",
            pickLine: "AC outlet, his own charger. Power only, no data.",
            u: 0.58,
            v: 0.505,
            xPct: 50,
            yPct: 52,
          },
          {
            id: "hs_public_usb",
            label: "Public USB port",
            detail: "A public USB charging port on the wall. Unlike an AC outlet, USB carries DATA as well as power \u2014 a modified cable or port (BadUSB) can act like a keyboard the moment it connects, with no file to open and no click to approve.",
            goblin: true,
            eventId: "charger_bad",
            outcome: "risky",
            u: 0.432,
            v: 0.494,
            xPct: 44,
            yPct: 62,
          },
        ],
      },
    },
    {
      id: "byod",
      kind: "byodlaptop",
      config: {
        deviceLabel: "Max's personal tablet",
        clock: "10:42",
        headline: "Download the game—or secure the tablet first?",
        body: "Max wants to download a new game for the journey. This personal tablet is unmanaged and 214 days behind on security updates.",
        osBanner: "System update available \u2014 23 security patches pending (last updated 214 days ago)",
        lenaSafe: "BYOD is not 'use anything.' Controls first, client files second.",
        lenaRisky: "A personal, un-patched device just became the company's weakest link.",
        voiceId: "onyx",
        language: "en-US",
        choices: [
          {
            id: "game",
            label: "Ignore the update and download the new game everyone's playing",
            kind: "game",
            eventId: "byod_bad",
            outcome: "risky",
            result: {
              title: "The new game was the entry point",
              body: "Max downloaded the unverified game, granted access to files and contacts, then opened it. The app connected to an attacker and uploaded the client deck.",
              bad: true,
            },
          },
          {
            id: "update",
            label: "Run the updates and open files in the managed work profile",
            kind: "update",
            safe: true,
            eventId: "byod_safe",
            outcome: "safe",
            result: {
              title: "Managed, patched, separated",
              body: "Updated, enrolled, and work data isolated in the work profile with remote wipe available. Now it is safe to open client files.",
              bad: false,
            },
          },
        ],
      },
    },
    {
      id: "ceocall",
      kind: "deepfakecall",
      config: {
        app: "WhatsApp Video",
        callerName: "Alex Rivera",
        callerSubtitle: "Chief Executive Officer",
        relationship: "Your CEO",
        avatarUrl: CEO_AVATAR,
        avatarInitials: "AR",
        voiceId: "onyx",
        language: "en-US",
        clips: {
          greeting: `${CDN}/clips/ceo_urgent.mp4`,
          request: `${CDN}/clips/ceo_request.mp4`,
          pressure: `${CDN}/clips/ceo_pressure.mp4`,
          deflect: `${CDN}/clips/ceo_deflect.mp4`,
          goodbyeFail: `${CDN}/clips/ceo_goodbye_fail.mp4`,
          goodbyeVerify: `${CDN}/clips/ceo_goodbye_verify.mp4`,
        },
        payment: {
          amount: "Client pricing file",
          recipient: "WhatsApp · Alex Rivera",
          reference: "MFA approve + send deck",
          deadline: "Immediately",
        },
        lines: CEO_LINES,
        artifacts: [
          "Blinking is unnatural — long gaps, then a rapid double-blink",
          "Mouth lags the words slightly (lip-sync drift)",
          "Background flickers when the head moves",
          "Urgency + WhatsApp + MFA in one breath",
          "Caller dodges any internal verification question",
        ],
        decision: {
          send: "Send the pricing file on WhatsApp now",
          ask: "Ask something only the real CEO would know",
          verify: "Hang up and verify through a trusted channel",
        },
      },
    },
    {
      id: "pc",
      kind: "publicpc",
      config: {
        headline: "The Business Centre PC",
        body: "Max checks his work email on the business centre PC. He reads two messages, then his shuttle is called.",
        emailUrl: "mail.corp-acme.com",
        lenaSafe: "Signed out. Session terminated server-side. The next guest gets a blank login page.",
        lenaRisky:
          "Closing the tab is not signing out. The session and the address he just typed both stay behind for the next guest.",
        voiceId: "onyx",
        language: "en-US",
      },
    },
    {
      // the business-centre PC again, in film mode (PublicPcFilm)
      id: "pcfilm",
      kind: "publicpc",
      config: {
        film: {
          url: "/cdn/sim/awareness-videos/travel-security/travel-security.mp4",
          desktopBrand: "Business Centre Guest PC",
          portalUrl: "learn.corp-acme.com/remote-work",
          portalBrand: "Corp Acme \u00b7 Security awareness",
          brand: "Security awareness",
          headline: "Working on the road",
          body: "Wi-Fi, screens, calls, a lost laptop, chargers and shared PCs: this short film sums up Max's day, and what to do instead.",
          playLabel: "Watch the video",
          skipLabel: "Skip the video",
          continueLabel: "Continue to quiz",
        },
        voiceId: "onyx",
        language: "en-US",
      },
    },
    {
      id: "timeline",
      kind: "incidenttimeline",
      config: {
        headline: "The Consequence Room",
        body: "Click each risk event from Max's day. See what happened, why it mattered, and what should have been done.",
        continueLabel: "Continue to the video",
        events: [
          {
            id: "ev_wifi",
            title: "Public Wi-Fi",
            // 2026-07-14: swapped raw emoji for the professional line-icon
            // set in ui/TimelineIcons.tsx — colour emoji rendered as
            // childish/inconsistent for a B2B senior-management audience.
            icon: "wifi",
            what: "Max joined a lookalike or open network and hit a fake login page.",
            why: "Evil twins and captive portals harvest credentials and session cookies.",
            fix: "Use a known phone hotspot or a company VPN on a verified network.",
          },
          {
            id: "ev_shoulder",
            title: "Shoulder surfing",
            icon: "eye",
            what: "Confidential pricing was visible to the passenger across the aisle.",
            why: "Screens in public are billboards. Numbers get photographed in seconds.",
            fix: "Privacy filter, better seat, lock when not typing — or wait until private.",
          },
          {
            id: "ev_call",
            title: "Public call",
            icon: "call",
            what: "Discount percentages and a CFO name were spoken aloud on the train.",
            why: "Anyone nearby can hear, record, or tip a competitor.",
            fix: "Call back from a private place, or keep it general with a headset.",
          },
          {
            id: "ev_laptop",
            title: "Forgotten laptop",
            icon: "laptop",
            what: "The bag stayed under the seat while Max rushed onto the platform.",
            why: "An unreported device stays unlocked to attackers and sessions stay live.",
            fix: "Report to IT immediately for remote lock, wipe, and session revoke.",
          },
          {
            id: "ev_charger",
            title: "Unknown charger",
            icon: "plug",
            what: "A public USB charging cable touched a work phone.",
            why: "Juice jacking can install malware or exfiltrate data over USB.",
            fix: "Use your own charger, cable, or power bank — never unknown USB.",
          },
          {
            id: "ev_byod",
            title: "BYOD misuse",
            icon: "mobile",
            what: "Client files were pushed onto an uncontrolled personal tablet.",
            why: "No MDM, no wipe, mixed personal apps — data walks out of policy.",
            fix: "Only use personal devices when BYOD rules and controls are active.",
          },
          {
            id: "ev_deepfake",
            title: "Deepfake request",
            icon: "faceid",
            what: "A fake CEO demanded WhatsApp files and an MFA approval.",
            why: "Urgency plus unusual channels bypass normal verification.",
            fix: "Hang up and verify on a known number or official directory channel.",
          },
          {
            id: "ev_pc",
            title: "Public PC left signed in",
            icon: "desktop",
            what: "Max checked work email on the hotel business centre PC, then just closed the tab.",
            why: "Closing a tab does not end a web session. The next guest can reopen it, signed in.",
            fix: "Always click Sign out on any shared or public computer, not just close the window.",
          },
        ],
      },
    },
  ],

  voiceAttackers: {
    max_remote: {
      persona: {
        name: "Max Berger",
        role: "Sales Manager (careless remote worker)",
        pretext: "remote_work_day",
        voiceId: "onyx",
        language: "en-US",
      },
      goal: "Get Max to the client meeting without turning small remote-work mistakes into a data incident.",
      opening: "Help Max get to his client meeting without causing a security incident.",
      allowedTopics: [
        "remote work packing",
        "public Wi-Fi",
        "shoulder surfing",
        "public phone calls",
        "lost devices",
        "chargers and USB",
        "BYOD controls",
        "deepfake verification",
      ],
      scoringEvents: [
        {
          id: "packed_safe",
          label: "Packed approved kit (privacy screen, headset, charger)",
          kind: "positive",
          weight: 15,
          detail:
            "Company-issued gear (privacy filter, headset, own charger) keeps the whole trip inside IT's control: encrypted, trackable, and safe to use on any network or in any seat.",
        },
        {
          id: "packed_personal",
          label: "Took personal device or skipped protection gear",
          kind: "risk",
          weight: 20,
          detail:
            "A personal laptop or phone has no MDM, no disk encryption guarantee, and no remote wipe. If it's lost or infected, IT has no way to contain the damage — and client data may already be unrecoverable.",
        },
        {
          id: "packed_usb",
          label: "Copied client folder onto a USB stick",
          kind: "risk",
          weight: 25,
          detail:
            "An unencrypted USB stick is a bare copy of client data with no access log, no remote wipe, and no audit trail. If it's lost on the train, the client folder is simply gone — and you won't even know when.",
        },
        {
          id: "wifi_trap",
          label: "Joined a trap / lookalike public Wi-Fi",
          kind: "risk",
          weight: 25,
          detail:
            "Fake hotspots with an official-sounding name (\"SBB-Free-WiFi-Premium\" etc.) let an attacker sit between you and the internet, capturing anything not protected by HTTPS and pushing fake login pages.",
        },
        {
          id: "wifi_hotspot",
          label: "Used Max's trusted phone hotspot",
          kind: "positive",
          weight: 20,
          detail:
            "A personal mobile hotspot is a network only you control — no unknown operator, no captive portal, no one else on the same access point. It's the safest default whenever public Wi-Fi is an option.",
        },
        {
          id: "shoulder_fail",
          label: "Exposed the pricing screen to nearby passengers",
          kind: "risk",
          weight: 25,
          detail:
            "Anyone within eyeline of an unshielded screen in a public seat can read confidential pricing, client names, or deal terms — and photograph it in seconds without you ever noticing.",
        },
        {
          id: "shoulder_safe",
          label: "Moved seat and used a privacy screen",
          kind: "positive",
          weight: 20,
          detail:
            "A privacy filter narrows the viewing angle so only you can read the screen straight-on, and picking a seat with your back to a wall removes the easiest shoulder-surfing angle entirely.",
        },
        {
          id: "call_disclose",
          label: "Spoke confidential deal details in public",
          kind: "risk",
          weight: 30,
          detail:
            "A phone call in a public carriage is audible to everyone nearby. Naming clients, prices, or deal terms out loud leaks the same information a screen would — just through a different sense.",
        },
        {
          id: "call_safe",
          label: "Kept the public call private or general",
          kind: "positive",
          weight: 20,
          detail:
            "Using a headset and sticking to generic phrasing (\"I'll confirm the numbers once I'm at my desk\") lets the call continue without handing sensitive specifics to anyone listening in.",
        },
        {
          id: "lost_wait",
          label: "Delayed reporting a lost laptop",
          kind: "risk",
          weight: 35,
          detail:
            "Every minute between losing a device and reporting it is a minute IT can't remote-lock or wipe it. Early reporting is the single biggest lever you have over how bad a lost-device incident becomes.",
        },
        {
          id: "lost_report",
          label: "Reported the lost device to IT immediately",
          kind: "positive",
          weight: 30,
          detail:
            "Reporting immediately lets IT remote-lock or wipe the device within minutes, often before anyone who finds it even gets past the lock screen. Speed is what turns a scare into a non-event.",
        },
        {
          id: "lost_found",
          label: "Looked around and recovered the forgotten laptop bag",
          kind: "positive",
          weight: 15,
          detail:
            "A quick look back before leaving the seat is the cheapest control there is — it catches the mistake before it ever becomes an incident that needs reporting at all.",
        },
        {
          id: "lost_linkedin",
          label: "Posted about a lost laptop on LinkedIn",
          kind: "risk",
          weight: 40,
          detail:
            "A public post broadcasts the loss to anyone, including whoever picked up the device, before IT has had a chance to lock it down — and invites fake \"I found it!\" scams targeting you directly.",
        },
        {
          id: "charger_bad",
          label: "Used the public USB charging cable",
          kind: "risk",
          weight: 25,
          detail:
            "USB carries both power and data. A tampered public cable or \"juice-jacking\" kiosk can silently install malware or pull files the moment the phone is plugged in — with no visible warning to the victim.",
        },
        {
          id: "charger_safe",
          label: "Charged with Max's own charger or the AC outlet",
          kind: "positive",
          weight: 15,
          detail:
            "A wall outlet only supplies power, no data pins at all. Your own charger and cable are trusted hardware you control end to end, so charging never becomes a data-transfer opportunity.",
        },
        {
          id: "byod_bad",
          label: "Used an uncontrolled personal device for work data",
          kind: "risk",
          weight: 30,
          detail:
            "A personal tablet with no MDM enrollment, no enforced encryption, and no company oversight is an unmanaged endpoint. Client files on it live outside every control IT has.",
        },
        {
          id: "byod_safe",
          label: "Required BYOD rules and controls before work use",
          kind: "positive",
          weight: 20,
          detail:
            "Enrolling a personal device in MDM before using it for work brings it under the same encryption, remote-wipe, and compliance policy as a company laptop — closing the gap BYOD normally opens.",
        },
        {
          id: "pc_session_left_open",
          label: "Closed a public PC's browser tab without signing out",
          kind: "risk",
          weight: 25,
          detail:
            "Closing the tab doesn't end the session. The next person to sit down can reopen the same site and land right inside your still-logged-in account — email, CRM, whatever you were using.",
        },
        {
          id: "pc_session_signed_out",
          label: "Signed out of the public PC before leaving",
          kind: "positive",
          weight: 15,
          detail:
            "Explicitly signing out invalidates the session server-side, so the next person at that machine gets a fresh login screen instead of your account.",
        },
        // DeepfakeCall.tsx records these hardcoded ids on send / verify.
        {
          id: "wired_money",
          label: "Sent files / approved MFA for the deepfake CEO",
          kind: "risk",
          weight: 70,
          detail:
            "AI voice/video cloning can now convincingly fake an executive on a live call. Acting on urgency alone — without verifying through a second, trusted channel — is exactly what these scams are built to exploit.",
        },
        {
          id: "verified_son",
          label: "Verified the CEO request on a trusted channel",
          kind: "positive",
          weight: 50,
          detail:
            "Hanging up and calling back on a known number (or messaging on a separate, already-trusted channel) breaks the attacker's only advantage: controlling the line you're talking on.",
        },
      ],
      forbidden: [],
      maxDurationS: 0,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Max Works Remotely\u2026\nUnfortunately",
      kicker: "Mission 01 \u00b7 Remote Day",
      guide: {
        title: "Help Max survive the day",
        body: "You coach Max Berger, a sales manager with a client meeting today. His day takes him by train, through a station café and a hotel before he reaches the client. It starts in his apartment, where he has to pack his bag.",
      },
    },

    // --- Scene 1: apartment / pack ---
    {
      id: "b_apt_establish",
      type: "camera",
      waypointId: "wp_apt_establish",
      environmentId: "apartment",
    },
    { id: "b_apt_desk", type: "camera", waypointId: "wp_apt_desk" },
    {
      id: "b_pack",
      type: "device",
      deviceId: "pack",
      inspectAllowed: false,
      guide: {
        title: "Remote work launch",
        body: "Drag to look around. Tap what Max should pack — then confirm the bag.",
      },
      // Pack markers + interaction live on PackRoom (device.config.plateHotspots).
    },
    {
      id: "b_teach_pack",
      type: "teach",
      title: "Remote work starts at home",
      teach: {
        body: "Remote work starts before leaving home. Approved devices, no unnecessary copies, and basic protection tools (privacy screen, headset, charger) keep Max from becoming a walking data breach.",
        spot: [
          "Personal tablets and random USB sticks are not 'being prepared.'",
          "Passwords on sticky notes travel with the laptop.",
          "Skipping a privacy screen guarantees a shoulder-surfing sequel.",
        ],
        protect: [
          "Pack company-approved devices only.",
          "Bring a privacy filter and headset for public spaces.",
          "Don't copy client folders onto personal media.",
        ],
        remember: "Preparation is a control — not an afterthought.",
      },
    },

    // --- Scene 2: platform / wifi ---
    {
      id: "b_title_platform",
      type: "title",
      title: "Train platform",
      guide: {
        title: "A few minutes later\u2026",
        body: "Max reaches the station. The client just updated the deck.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_plat_establish",
      type: "camera",
      waypointId: "wp_plat_establish",
      environmentId: "platform_kiosk",
    },
    { id: "b_plat_kiosk", type: "camera", waypointId: "wp_plat_kiosk" },
    { id: "b_plat_screen", type: "camera", waypointId: "wp_plat_screen" },
    {
      id: "b_wifi",
      type: "device",
      deviceId: "wifi",
    },
    {
      id: "b_teach_wifi",
      type: "teach",
      title: "Mystery Wi-Fi is not a feature",
      teach: {
        body: "Lookalike SSIDs and 'fast free' networks often lead to captive portals that harvest company credentials. A known phone hotspot (or VPN on a verified network) beats enthusiasm in the network name.",
        spot: [
          "SBB-Free-WiFi-Fast and ClientMeetingVIP are classic evil-twin bait.",
          "A login page asking for work email + password on open Wi-Fi is a red flag.",
          "Friendly strangers recommending a network are not a trust model.",
        ],
        protect: [
          "Prefer your own hotspot for work downloads.",
          "Use a company VPN when you must use public Wi-Fi.",
          "Verify official network names — don't trust lookalikes.",
        ],
        remember: "Known hotspot beats mystery Wi-Fi every time.",
      },
    },

    // --- Scene 3–4: carriage / shoulder + public call ---
    {
      id: "b_title_carriage",
      type: "title",
      title: "Train carriage",
      guide: {
        title: "All aboard",
        body: "Max finds a table seat. The Curious Passenger finds Max.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_train_establish",
      type: "camera",
      waypointId: "wp_train_establish",
      environmentId: "carriage",
    },
    { id: "b_train_seat", type: "camera", waypointId: "wp_train_seat" },
    { id: "b_train_watch", type: "camera", waypointId: "wp_train_passenger" },
    {
      id: "b_shoulder",
      type: "device",
      deviceId: "shoulder",
      guide: {
        title: "Other passengers can see",
        body: "Drag around the real 3D carriage. Check the passengers, windows, and sightlines before opening the pricing file.",
      },
    },
    {
      // 2026-07-14: split from one generic "b_teach_shoulder" into three
      // outcome-specific beats (open/zoom/bright) -- see the option
      // outcome keys above and the edges further down. Each one explains
      // the SPECIFIC mistake that choice made, not a one-size-fits-all
      // "screens are public" lecture.
      id: "b_teach_shoulder_open",
      type: "teach",
      title: "Wrong move — opened it before checking the room",
      teach: {
        body: "Max opened the confidential pricing file the moment he sat down, without checking who could see the screen first. The problem isn't the file itself — it's opening it in a carriage full of strangers with a clear line of sight to the display.",
        spot: [
          "The passenger across the aisle had a direct, unobstructed view of the screen.",
          "Nothing about the seat or the sightlines was checked before the file was opened.",
          "The instant sensitive numbers are on screen, anyone nearby can read and remember them.",
        ],
        protect: [
          "Check your surroundings BEFORE opening anything confidential, not after.",
          "Move to a seat with a wall or fewer sightlines first.",
          "Put the privacy screen on before sensitive work starts, not after.",
        ],
        remember: "Assess the room before you open the file — not the other way round.",
      },
    },
    {
      id: "b_teach_shoulder_zoom",
      type: "teach",
      title: "Wrong move — zoomed in for everyone, not just Max",
      teach: {
        body: "Zooming in to make the pricing numbers easier for Max to read also makes them easier for every passenger with a view of the screen to read. Small, hard-to-read text was accidentally doing part of the protecting — and that protection just got zoomed away.",
        spot: [
          "A bigger font is bigger for the whole carriage, not just for Max.",
          "Shoulder surfers rarely need long — a few readable seconds is enough to remember a number.",
          "Zooming in didn't fix the real problem: the screen was still visible to strangers.",
        ],
        protect: [
          "If text is hard to read in public, that's a cue to move somewhere private — not to zoom in.",
          "Use a privacy filter so content stays unreadable at an angle regardless of zoom level.",
          "Save detailed review of sensitive numbers for a private screen.",
        ],
        remember: "Making it easier to read makes it easier to steal.",
      },
    },
    {
      id: "b_teach_shoulder_bright",
      type: "teach",
      title: "Wrong move — lit it up for the whole carriage",
      teach: {
        body: "Turning brightness to maximum made the pricing document easier to see from much wider angles and further away — including from seats behind and across the aisle. Full brightness in public is a spotlight, not a convenience.",
        spot: [
          "Maximum brightness widens the usable viewing angle for anyone nearby.",
          "A brighter screen is also easier to photograph discreetly from a distance.",
          "Comfort for Max came at the cost of visibility for everyone else in the carriage.",
        ],
        protect: [
          "Keep brightness only as high as YOU need it, not maxed out.",
          "Use a privacy filter — it cuts the viewing angle regardless of brightness.",
          "If glare is the real issue, change seats or angle the screen away from others.",
        ],
        remember: "A brighter screen in public means a bigger audience, not a better view.",
      },
    },
    {
      id: "b_teach_shoulder_safe",
      type: "teach",
      title: "Good call — that's how you protect a screen",
      teach: {
        outcome: "safe",
        body: "Moving to a more private seat and putting up the privacy screen was the right instinct. A busy carriage full of strangers is exactly the wrong place to leave a pricing document in plain view.",
        spot: [
          "Window reflections can still reveal a screen to people you never face — check them too.",
          "A privacy screen narrows the viewing angle, it doesn't make the screen invisible up close.",
          "The safest seat can still be overheard, not just overlooked — keep sensitive calls private as well.",
        ],
        protect: [
          "Keep the privacy filter on for any confidential document, not just this one.",
          "Pick aisle-side or wall-backed seats for sensitive work when you have the choice.",
          "Lock the screen the moment you step away, even for a minute.",
        ],
        remember: "If strangers can read it, don't open it — even after you've found a better seat.",
      },
    },
    {
      id: "b_pubcall",
      type: "device",
      deviceId: "pubcall",
      guide: {
        title: "Say it louder, Max",
        body: "Nina is calling about the deal. Other passengers can hear. Choose Max's reply.",
      },
    },
    {
      id: "b_teach_call",
      type: "teach",
      title: "Public means public",
      teach: {
        body: "Discounts, CFO names, and contract details spoken on a train are free intelligence for anyone nearby. Postpone sensitive calls, or keep them general with a headset.",
        spot: [
          "Speakerphone turns the carriage into a conference room.",
          "Specific percentages and names are the highest-value leaks.",
          "Colleagues will wait for a private callback.",
        ],
        protect: [
          "Call back from somewhere private.",
          "Use a headset and stay high-level if you must talk.",
          "Never put confidential calls on speaker in public.",
        ],
        remember: "If you wouldn't pin it on a wall, don't say it on a train.",
      },
    },
    {
      id: "b_teach_call_safe",
      type: "teach",
      title: "Good call — that's how you keep a call private",
      teach: {
        outcome: "safe",
        body: "Moving the call somewhere private, or keeping it general on a headset, was the right instinct. Discounts, CFO names, and contract details are free intelligence the moment they're said out loud near strangers.",
        spot: [
          "A headset keeps your side quiet, but you can still repeat details out loud without noticing.",
          "\"General\" can drift into specifics once the conversation gets going — stay deliberate.",
          "Colleagues would rather wait five minutes for a callback than have the deal discussed on a train.",
        ],
        protect: [
          "Call back from somewhere private whenever you can.",
          "If you must talk now, keep it high-level and skip names, numbers, and terms.",
          "Never put a confidential call on speaker in public — headset or nothing.",
        ],
        remember: "If you wouldn't pin it on a wall, don't say it on a train — even on a headset.",
      },
    },

    // --- Scene 5: arrival / lost device ---
    {
      id: "b_title_arrival",
      type: "title",
      title: "Arrival",
      guide: {
        title: "Max is in a hurry",
        body: "He bolts for the exit. Something stays behind.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_lost_establish",
      type: "camera",
      waypointId: "wp_plat_establish",
      environmentId: "platform",
    },
    { id: "b_lost_train", type: "camera", waypointId: "wp_lost_train" },
    {
      id: "b_lost",
      type: "device",
      deviceId: "lost",
      guide: {
        title: "The laptop that wanted freedom",
        body: "Max is on the platform — the train is about to leave with his laptop bag still on board. Report it, don't advertise it.",
      },
    },
    // 2026-07-14: split from one generic "b_teach_lost" into three
    // outcome-specific beats (wait/linkedin/delay) -- see the option
    // outcome keys above and the edges further down.
    {
      id: "b_teach_lost_wait",
      type: "teach",
      title: "Wrong move — waiting burned the response window",
      teach: {
        body: "Max decided to wait and hope someone returns the bag, instead of reporting it immediately. Every minute that passes before IT knows is a minute the laptop's active sessions, saved passwords, and local files stay exposed to whoever finds it.",
        spot: [
          "Hoping for a good outcome is not a response plan.",
          "The train kept moving while Max waited — the window to act was closing in real time.",
          "IT can't lock, wipe, or revoke anything for a device they don't know is missing.",
        ],
        protect: [
          "Report a missing device to IT/security the moment you notice — don't wait to see what happens.",
          "Ask for remote lock and session revocation immediately; it costs nothing to ask and be wrong.",
          "Treat \"it'll probably turn up\" as a red flag, not a plan.",
        ],
        remember: "Waiting doesn't recover the laptop — it just recovers less of it.",
      },
    },
    {
      id: "b_teach_lost_linkedin",
      type: "teach",
      title: "Wrong move — told the internet before telling IT",
      teach: {
        body: "Posting \"Lost laptop, anyone seen it?\" on LinkedIn broadcasts to the entire internet — including anyone who might have picked up the bag — that a company device with an active session is unattended, before IT even knows about it.",
        spot: [
          "A public post reaches far more people than the one honest finder you're hoping for.",
          "Anyone who took the bag on purpose now knows no one has reported it yet.",
          "Client names, project details, or branding visible in a post photo can leak on their own.",
        ],
        protect: [
          "Report to IT/security FIRST, always — social media is not an incident channel.",
          "Never publicly announce a lost company device before it's locked and sessions are revoked.",
          "If you need to ask around, do it through official internal channels, not public posts.",
        ],
        remember: "A public \"lost device\" post is a welcome sign for the wrong person.",
      },
    },
    {
      id: "b_teach_lost_delay",
      type: "teach",
      title: "Wrong move — silence made it worse, not better",
      teach: {
        body: "Asking the client to delay and saying nothing internally trades a few minutes of awkwardness now for a much bigger incident later. The laptop's sessions and files stay exposed for as long as IT is kept in the dark.",
        spot: [
          "Protecting the client relationship short-term isn't worth an unmonitored security exposure.",
          "Every minute of silence is a minute IT can't lock the device or revoke its sessions.",
          "Hiding an incident to \"not delay the client\" almost always makes the eventual disclosure worse.",
        ],
        protect: [
          "Report immediately — the client can wait a few minutes for an update; exposed company data can't.",
          "Separate \"managing client comms\" from \"reporting a security incident\" — they don't have to conflict.",
          "When in doubt, report first and manage the message second.",
        ],
        remember: "Silence doesn't protect the client relationship — it just delays finding out how bad it got.",
      },
    },

    {
      id: "b_teach_lost_safe",
      type: "teach",
      title: "Good call — that's damage control",
      teach: {
        outcome: "safe",
        body: "Reporting the laptop immediately (or securing it and reporting the near miss) was the right move. IT can only lock the device, revoke active sessions, and reset credentials while the window is still open — every minute of delay hands that window to whoever finds the bag next.",
        spot: [
          "A found bag can still have been opened in the seconds before you got to it — reporting the near miss lets IT check for that.",
          "Waiting \"just a few more minutes\" before calling it in is how the response window quietly closes.",
          "Even a locked laptop keeps active login sessions alive until someone revokes them remotely.",
        ],
        protect: [
          "Call IT/security the moment a company device goes missing — even briefly.",
          "Ask for remote lock and session revocation as standard first steps.",
          "Report near misses too, not just confirmed losses — it's still useful signal.",
        ],
        remember: "Early reporting is damage control, whether the laptop is lost or just briefly out of sight.",
      },
    },

    // --- Scene 6–7: cafe / charger + BYOD ---
    {
      id: "b_title_cafe",
      type: "title",
      title: "Station café",
      guide: {
        title: "Battery at 4%",
        body: "MFA still needed. A friendly stranger has a cable.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_cafe_establish",
      type: "camera",
      waypointId: "wp_cafe_establish",
      environmentId: "cafe",
    },
    { id: "b_cafe_table", type: "camera", waypointId: "wp_cafe_table" },
    {
      id: "b_charger",
      type: "device",
      deviceId: "charger",
      guide: {
        title: "The Charger of Doom",
        body: "The real objects are visible: Max's charger on the table, the wall AC outlet, and the public USB cable. Choose.",
      },
    },
    {
      id: "b_teach_charger",
      type: "teach",
      title: "Not every cable is your friend",
      teach: {
        body: "Public USB charging ports can install malware or exfiltrate data (juice jacking). Max's own charger or a normal AC outlet is boring and correct.",
        spot: [
          "The cable hanging from a public USB panel can carry data as well as power.",
          "Public USB ports are data ports, not just power.",
          "A normal AC socket plus your own charger exposes no USB data path.",
        ],
        protect: [
          "Carry your own cable and power bank.",
          "Prefer AC outlets with your own charger.",
          "Never plug work phones into unknown USB.",
        ],
        remember: "Power is fine. Unknown USB is not.",
      },
    },
    {
      id: "b_teach_charger_safe",
      type: "teach",
      title: "Good call — that's how you avoid juice jacking",
      teach: {
        outcome: "safe",
        body: "Using Max's own charger and a normal AC outlet instead of the public USB cable was the right call. Public USB charging ports can carry data as well as power (juice jacking) — a normal socket exposes no USB data path at all.",
        spot: [
          "A \"free\" USB cable at a table or kiosk can still carry data even if it charges fine.",
          "Juice-jacking hardware is cheap and easy to hide behind a wall panel or charging station.",
          "Borrowing someone else's cable is the same risk as an unknown public USB port.",
        ],
        protect: [
          "Keep carrying your own cable and a power bank as the default.",
          "Prefer AC outlets with your own charger over any shared USB port.",
          "If you must use an unknown USB port, use a USB data-blocker (\"USB condom\") first.",
        ],
        remember: "Power is fine. Unknown USB is not — even when your own charger just isn't handy.",
      },
    },
    { id: "b_byod_focus", type: "camera", waypointId: "wp_byod_table" },
    {
      id: "b_byod",
      type: "device",
      deviceId: "byod",
      guide: {
        title: "Bring Your Own Disaster",
        body: "Laptop gone — only the personal tablet on the table. Click the glowing \u201cMax's personal tablet \u00b7 Pick it up\u201d label on the table to continue.",
      },
    },
    {
      id: "b_teach_byod",
      type: "teach",
      title: "BYOD is a policy, not a vibe",
      teach: {
        body: "Personal devices need company-approved controls: lock, updates, work profile separation, remote wipe, and approved apps. Trusting yourself is not a control. Screenshots to messaging apps are exfiltration with extra steps.",
        spot: [
          "Outdated OS and mixed personal apps are default on home tablets.",
          "Copying to personal email bypasses DLP and retention.",
          "Missing MDM means you can't wipe work data if the tablet is lost.",
        ],
        protect: [
          "Follow BYOD enrollment before opening work files.",
          "Keep work data in managed apps / work profile only.",
          "If controls fail the check, wait for a managed device.",
        ],
        remember: "No controls, no client files.",
      },
    },
    {
      id: "b_teach_byod_safe",
      type: "teach",
      title: "Good call — that's what a real control looks like",
      teach: {
        outcome: "safe",
        body: "Waiting for a managed, updated device — instead of trusting a personal tablet with client files — was the right call. Personal devices need company-approved controls: lock, updates, work-profile separation, and remote wipe. Trusting yourself is not one of those controls.",
        spot: [
          "A tablet can look fine and still be months behind on security patches.",
          "\"Just this once\" on a personal device is how BYOD exceptions become the norm.",
          "Screenshotting a file to a messaging app is exfiltration with extra steps, even from a well-meaning tablet.",
        ],
        protect: [
          "Follow BYOD enrollment before opening any work file on a personal device.",
          "Keep work data inside managed apps / a work profile only.",
          "If controls fail the check, wait for a managed device rather than working around it.",
        ],
        remember: "No controls, no client files — waiting for the right device is the control working as intended.",
      },
    },

    // --- Scene 8: lobby / deepfake CEO ---
    {
      id: "b_title_lobby",
      type: "title",
      title: "Hotel lobby",
      guide: {
        title: "Urgent video call",
        body: "The CEO wants a file on WhatsApp. Something feels off.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_lobby_establish",
      type: "camera",
      waypointId: "wp_lobby_establish",
      environmentId: "lobby",
    },
    { id: "b_lobby_phone", type: "camera", waypointId: "wp_lobby_phone" },
    {
      id: "b_deepfake",
      type: "device",
      deviceId: "ceocall",
      guide: {
        title: "The deepfake boss",
        body: "Answer the call. Look for tells. Verify unusual requests on a channel you trust.",
      },
    },
    {
      id: "b_teach_deepfake_fail",
      type: "teach",
      title: "Deepfake successful",
      teach: {
        body: "Deepfake successful. Max has now promoted himself to Chief Incident Officer. Urgency, WhatsApp file transfer, and MFA approval in one breath is a classic remote-work scam — verify on a separate trusted channel first.",
        spot: [
          "Unusual channel (WhatsApp) for company files.",
          "Pressure to approve MFA during the same call.",
          "Visual/audio tells and dodged verification questions.",
        ],
        protect: [
          "Hang up and call the CEO via the company directory.",
          "Never approve MFA because a video asked you to.",
          "Unusual + urgent + secrecy = verify independently.",
        ],
        remember: "See a face. Still verify the request.",
      },
    },
    {
      id: "b_teach_deepfake_win",
      type: "teach",
      title: "Good call — that wasn't the CEO",
      teach: {
        // 2026-07-14: this was missing `outcome: "safe"`, so TeachOverlay
        // defaulted to the "risky" framing (red "!" / "ATTACK · What is
        // going on?") for the CORRECT choice -- reported as "no feedback"
        // because a good decision looked identical to the failure card.
        outcome: "safe",
        body: "Hanging up and calling back on a known number was the right instinct. The real CEO replies: \"I did not call you. Also, why would I ask for files on WhatsApp?\" Separate-channel verification stops digital impersonation cold.",
        spot: [
          "Video presence alone is not proof of identity — deepfakes are getting cheaper to fake convincingly.",
          "A surprise call on an unusual channel (WhatsApp) is still worth double-checking even when it looks routine.",
          "MFA prompts during unexpected calls are often push-fatigue attacks — the same instinct applies there too.",
        ],
        protect: [
          "Keep verifying unusual requests on a trusted, known channel — every time, not just when something feels off.",
          "Reject MFA prompts you did not initiate, no matter who appears to be asking.",
          "Report deepfake / impersonation attempts to security so others can be warned.",
        ],
        remember: "Urgency is not authentication — verifying is always worth the extra minute.",
      },
    },

    // --- Scene 8: business centre public PC ---
    {
      id: "b_title_business",
      type: "title",
      title: "Business centre",
      guide: {
        title: "Max's phone is low again",
        body: "He checks work email on the hotel's shared guest PC instead.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_biz_establish",
      type: "camera",
      waypointId: "wp_biz_establish",
      environmentId: "business_center",
    },
    { id: "b_biz_approach", type: "camera", waypointId: "wp_biz_approach" },
    { id: "b_biz_screen", type: "camera", waypointId: "wp_biz_screen" },
    {
      id: "b_biz_pc",
      type: "device",
      deviceId: "pc",
      guide: {
        title: "The business centre PC",
        body: "Log in, read the mail, then decide how Max leaves the shared PC.",
      },
    },
    {
      id: "b_teach_biz",
      type: "teach",
      title: "The session stayed open",
      teach: {
        body: "Closing the browser tab is not the same as signing out. The web session and the typed address both stayed on the shared PC for the next guest.",
        spot: [
          "No explicit sign-out before leaving a shared device.",
          "A live session cookie survives closing the tab or window.",
          "Browser history/autofill retains the internal address typed.",
        ],
        protect: [
          "Always click Sign out on any shared or public computer.",
          "Prefer a private browsing window on shared devices.",
          "Clear history/autofill if a private window was not used.",
        ],
        remember: "Closing the window is not logging off.",
      },
    },
    {
      id: "b_teach_biz_safe",
      type: "teach",
      title: "Good call — that's how you leave a shared PC",
      teach: {
        outcome: "safe",
        body: "Signing out before walking away from the shared business-centre PC was the right move. Closing the browser tab is not the same as signing out — the web session and the typed address both would have stayed behind for the next guest otherwise.",
        spot: [
          "A signed-out session can still leave autofill or history behind if a private window wasn't used.",
          "Shared PCs are shared by design — assume the next person will poke around.",
          "A quick check-in tomorrow is still worth it if anything about the PC felt off.",
        ],
        protect: [
          "Always click Sign out on any shared or public computer, every time.",
          "Prefer a private browsing window on shared devices from the start.",
          "Clear history/autofill afterwards if a private window wasn't used.",
        ],
        remember: "Closing the window is not logging off — signing out is the step that actually matters.",
      },
    },

    // --- Scene 9: meeting / timeline ---
    {
      id: "b_title_meeting",
      type: "title",
      title: "Client meeting",
      guide: {
        title: "The consequence room",
        body: "Time to review how Max's day stacked up.",
      },
      autoAdvanceMs: 2200,
    },
    {
      id: "b_meet_establish",
      type: "camera",
      waypointId: "wp_meet_establish",
      environmentId: "meeting",
    },
    {
      id: "b_timeline",
      type: "device",
      deviceId: "timeline",
      guide: {
        title: "Review the day",
        body: "Open each event on the timeline — what happened, why it mattered, what to do next time.",
      },
    },

    // --- Scene 10: the awareness film on the business-centre PC ---
    {
      id: "b_film_title",
      type: "title",
      title: "One last stop",
      endsWhatIf: true,
      autoAdvanceMs: 5000,
      guide: { title: "One last stop", body: "Security has sent Max a short film about working on the road. He watches it on the business centre PC." },
    },
    { id: "b_film_biz_establish", type: "camera", waypointId: "wp_biz_establish", environmentId: "business_center" },
    { id: "b_film_biz_approach", type: "camera", waypointId: "wp_biz_approach" },
    { id: "b_film_biz_screen", type: "camera", waypointId: "wp_biz_screen" },
    {
      id: "b_film_pc",
      type: "device",
      deviceId: "pcfilm",
      guide: {
        title: "Wake the PC",
        body: "Click to power on the PC.",
        actionLabel: "Click to power on the PC",
      },
    },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Fix Max",
      quiz: [
        {
          id: "q1",
          title: "Public call",
          prompt:
            "You need to take a confidential call in public. What is safest?",
          hint: "Think about who else can hear names and numbers.",
          options: [
            {
              id: "a",
              label: "Put the call on speaker so you can take notes",
            },
            {
              id: "b",
              label:
                "Move somewhere private, or keep it general with a headset",
              correct: true,
            },
            {
              id: "c",
              label: "Confirm discounts and contact names quickly so the call is short",
            },
          ],
          rationale:
            "Public spaces leak audio. Postpone sensitive details or stay high-level with a headset — never speakerphone with names and numbers.",
        },
        {
          id: "q2",
          title: "Lost laptop",
          prompt: "You lose your work laptop. First action?",
          hint: "Speed matters more than optimism.",
          options: [
            {
              id: "a",
              label: "Wait a day in case someone returns it",
            },
            {
              id: "b",
              label: "Report immediately to IT/security",
              correct: true,
            },
            {
              id: "c",
              label: "Post on LinkedIn asking if anyone has seen it",
            },
          ],
          rationale:
            "Early reporting enables remote lock, wipe, and session revoke. Waiting or public posts make the incident worse.",
        },
        {
          id: "q3",
          title: "Public Wi-Fi",
          prompt:
            "You see a Wi-Fi network with a familiar-looking name. What should you do?",
          hint: "Lookalikes love the word Fast.",
          options: [
            {
              id: "a",
              label: "Join it — familiar names are usually official",
            },
            {
              id: "b",
              label: "Use a trusted hotspot/VPN or verify the official network",
              correct: true,
            },
            {
              id: "c",
              label: "Ask a nearby stranger which network is best",
            },
          ],
          rationale:
            "Evil twins mimic official SSIDs. Prefer your hotspot or VPN, and verify the real network name — don't trust enthusiasm in the SSID.",
        },
        {
          id: "q4",
          title: "BYOD",
          prompt: "You want to use a personal tablet for work. What must be true?",
          hint: "Trust is not a control.",
          options: [
            {
              id: "a",
              label: "You own the tablet, so it's fine",
            },
            {
              id: "b",
              label:
                "Company BYOD rules allow it and required security controls are active",
              correct: true,
            },
            {
              id: "c",
              label: "You can screenshot files into a messaging app instead",
            },
          ],
          rationale:
            "BYOD requires policy plus controls (lock, updates, work profile, remote wipe). Ownership alone is not enough.",
        },
        {
          id: "q5",
          title: "CEO verify",
          prompt:
            "Your CEO urgently asks for a file through an unusual channel. What should you do?",
          hint: "Video is not verification.",
          options: [
            {
              id: "a",
              label: "Send it immediately — you can see it's the CEO",
            },
            {
              id: "b",
              label: "Verify through a separate trusted channel",
              correct: true,
            },
            {
              id: "c",
              label: "Approve the MFA prompt first, then send the file",
            },
          ],
          rationale:
            "Deepfakes and impersonation thrive on urgency. Hang up and confirm via a known number or official directory before files or MFA.",
        },
      ],
    },

    {
      id: "b_debrief",
      type: "debrief",
      title: "Remote Work Is Still Work",
      guide: {
        title: "Remote Work Is Still Work",
        body: "Working from trains, cafés, hotels, and home is normal. The risk is not remote work itself — it's forgetting that company data travels with you. Lena: \"Max has completed remote work training. Unfortunately, he is still allowed near laptops.\"",
      },
    },
  ],

  edges: [
    { from: "b_intro", to: "b_apt_establish" },
    { from: "b_apt_establish", to: "b_apt_desk" },
    { from: "b_apt_desk", to: "b_pack" },
    { from: "b_pack", to: "b_teach_pack", on: "risky" },
    { from: "b_pack", to: "b_teach_pack", on: "safe" },
    { from: "b_teach_pack", to: "b_title_platform" },

    { from: "b_title_platform", to: "b_plat_establish" },
    { from: "b_plat_establish", to: "b_plat_kiosk" },
    { from: "b_plat_kiosk", to: "b_plat_screen" },
    { from: "b_plat_screen", to: "b_wifi" },
    { from: "b_wifi", to: "b_teach_wifi", on: "risky" },
    { from: "b_wifi", to: "b_title_carriage", on: "safe" },
    { from: "b_teach_wifi", to: "b_title_carriage" },

    { from: "b_title_carriage", to: "b_train_establish" },
    { from: "b_train_establish", to: "b_train_seat" },
    { from: "b_train_seat", to: "b_train_watch" },
    { from: "b_train_watch", to: "b_shoulder" },
    { from: "b_shoulder", to: "b_teach_shoulder_open", on: "risky_open" },
    { from: "b_shoulder", to: "b_teach_shoulder_zoom", on: "risky_zoom" },
    { from: "b_shoulder", to: "b_teach_shoulder_bright", on: "risky_bright" },
    { from: "b_shoulder", to: "b_teach_shoulder_safe", on: "safe" },
    { from: "b_teach_shoulder_open", to: "b_pubcall" },
    { from: "b_teach_shoulder_zoom", to: "b_pubcall" },
    { from: "b_teach_shoulder_bright", to: "b_pubcall" },
    { from: "b_teach_shoulder_safe", to: "b_pubcall" },

    { from: "b_pubcall", to: "b_teach_call", on: "risky" },
    { from: "b_pubcall", to: "b_teach_call_safe", on: "safe" },
    { from: "b_teach_call", to: "b_title_arrival" },
    { from: "b_teach_call_safe", to: "b_title_arrival" },

    { from: "b_title_arrival", to: "b_lost_establish" },
    { from: "b_lost_establish", to: "b_lost_train" },
    { from: "b_lost_train", to: "b_lost" },
    { from: "b_lost", to: "b_teach_lost_wait", on: "risky_wait" },
    { from: "b_lost", to: "b_teach_lost_linkedin", on: "risky_linkedin" },
    { from: "b_lost", to: "b_teach_lost_delay", on: "risky_delay" },
    { from: "b_lost", to: "b_teach_lost_safe", on: "safe" },
    { from: "b_teach_lost_wait", to: "b_title_cafe" },
    { from: "b_teach_lost_linkedin", to: "b_title_cafe" },
    { from: "b_teach_lost_delay", to: "b_title_cafe" },
    { from: "b_teach_lost_safe", to: "b_title_cafe" },

    { from: "b_title_cafe", to: "b_cafe_establish" },
    { from: "b_cafe_establish", to: "b_cafe_table" },
    { from: "b_cafe_table", to: "b_charger" },
    { from: "b_charger", to: "b_teach_charger", on: "risky" },
    { from: "b_charger", to: "b_teach_charger_safe", on: "safe" },
    { from: "b_teach_charger", to: "b_byod_focus" },
    { from: "b_teach_charger_safe", to: "b_byod_focus" },
    { from: "b_byod_focus", to: "b_byod" },

    { from: "b_byod", to: "b_teach_byod", on: "risky" },
    { from: "b_byod", to: "b_teach_byod_safe", on: "safe" },
    { from: "b_teach_byod", to: "b_title_lobby" },
    { from: "b_teach_byod_safe", to: "b_title_lobby" },

    { from: "b_title_lobby", to: "b_lobby_establish" },
    { from: "b_lobby_establish", to: "b_lobby_phone" },
    { from: "b_lobby_phone", to: "b_deepfake" },
    { from: "b_deepfake", to: "b_teach_deepfake_fail", on: "send" },
    { from: "b_deepfake", to: "b_teach_deepfake_win", on: "verify" },
    // ask re-chooses inside the device; default covers any other end key
    { from: "b_deepfake", to: "b_teach_deepfake_win" },
    { from: "b_teach_deepfake_fail", to: "b_title_business" },
    { from: "b_teach_deepfake_win", to: "b_title_business" },

    { from: "b_title_business", to: "b_biz_establish" },
    { from: "b_biz_establish", to: "b_biz_approach" },
    { from: "b_biz_approach", to: "b_biz_screen" },
    { from: "b_biz_screen", to: "b_biz_pc" },
    { from: "b_biz_pc", to: "b_teach_biz", on: "risky" },
    { from: "b_biz_pc", to: "b_teach_biz_safe", on: "safe" },
    { from: "b_teach_biz", to: "b_title_meeting" },
    { from: "b_teach_biz_safe", to: "b_title_meeting" },

    { from: "b_title_meeting", to: "b_meet_establish" },
    { from: "b_meet_establish", to: "b_timeline" },
    { from: "b_timeline", to: "b_film_title" },
    { from: "b_film_title", to: "b_film_biz_establish" },
    { from: "b_film_biz_establish", to: "b_film_biz_approach" },
    { from: "b_film_biz_approach", to: "b_film_biz_screen" },
    { from: "b_film_biz_screen", to: "b_film_pc" },
    { from: "b_film_pc", to: "b_quiz" },
    { from: "b_quiz", to: "b_debrief" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
